import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ModelName, MutationMethodForModel, ParamsForMethod, ReturnForMethod } from "../types";
import {
  TellescopeMutationInternalContext,
  TellescopeMutationSideEffectStrategy,
  UseTellescopeMutationContext,
  UseTellescopeMutationOptions,
} from "./types";
import { useMemo } from "react";
import { buildTellescopeQueryKey } from "../keys";
import { addKeyToMutationResult, executeTellescopeApiMethod } from "../utils";
import { useTellescopeSession, useTellescopeTanstackConfigContext } from "../context";
import { rollbackCacheUpdates } from "../cache";
import { executeTellescopeMutationSideEffect } from "../mappings";

/**
 * A hook for performing Tellescope API mutations with automatic cache management.
 * Provides built-in support for optimistic updates, automatic cache invalidation/updates,
 * and rollback on error.
 *
 * @template Model - The Tellescope model name
 * @template Method - The mutation method for the model (e.g., "createOne", "updateOne", "deleteOne")
 * @template TError - The error type that can be thrown (defaults to Error)
 * @template TOnMutateResult - The external context type for custom mutation logic (defaults to undefined)
 *
 * @param options - Configuration object with the following properties:
 * @param options.model - The name of the Tellescope model to mutate (e.g., "users", "appointments")
 * @param options.method - The mutation method to call on the model (e.g., "createOne", "updateOne", "deleteOne")
 * @param options.session - Optional session for authentication (uses context session if not provided)
 * @param options.autoCacheUpdateStrategy - Strategy for automatic cache updates after mutation completes. Can be:
 *   - `"update"` - Directly update related query caches with new data using configured updaters.
 *     If filters are configured but no updater is provided, falls back to invalidation for those queries.
 *   - `"invalidate"` - Invalidate affected queries to trigger refetch (default)
 *   - `"disabled"` - Skip automatic cache updates
 * @param options.optimisticUpdates - Whether to apply optimistic updates before mutation completes. Can be:
 *   - `true` - Enable optimistic updates for all affected queries
 *   - `false` - Disable optimistic updates (default)
 *
 * @returns A mutation object with mutate/mutateAsync functions and mutation state, including the auto-generated mutation key
 *
 * @example
 * ```tsx
 * // Basic usage
 * const createUser = useTellescopeMutation({
 *   model: "users",
 *   method: "createOne",
 * });
 *
 * // Create a user
 * createUser.mutate({
 *   fname: "John",
 *   lname: "Doe",
 *   email: "john@example.com",
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With cache invalidation strategy
 * const updateUser = useTellescopeMutation({
 *   model: "users",
 *   method: "updateOne",
 *   autoCacheUpdateStrategy: "invalidate",
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With optimistic updates disabled
 * const deleteUser = useTellescopeMutation({
 *   model: "users",
 *   method: "deleteOne",
 *   optimisticUpdates: false,
 *   autoCacheUpdateStrategy: "invalidate",
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With custom mutation side effects
 * const updateUser = useTellescopeMutation({
 *   model: "users",
 *   method: "updateOne",
 *   onMutate: async (variables) => {
 *     // Optionally return context for use in other callbacks
 *     return { startTime: Date.now() };
 *   },
 *   onSuccess: (data, variables, context) => {
 *     console.log("User updated successfully", data);
 *   },
 *   onError: (error, variables, context) => {
 *     console.error("Failed to update user", error);
 *     // Automatic rollback is handled internally
 *   },
 *   onSettled: (data, error, variables, context) => {
 *     console.log(`Mutation took ${Date.now() - context.startTime}ms`);
 *   },
 * });
 * ```
 */
export const useTellescopeMutation = <
  Model extends ModelName,
  Method extends MutationMethodForModel<Model>,
  TError = Error,
  TOnMutateResult = undefined,
  TStrategy extends TellescopeMutationSideEffectStrategy = "invalidate",
>({
  model,
  method,
  session: providedSession,
  autoCacheUpdateStrategy,
  optimisticUpdates,
  onMutate,
  onSuccess,
  onError,
  onSettled,
  ...tanstackOptions
}: UseTellescopeMutationOptions<Model, Method, TError, TOnMutateResult | undefined, TStrategy>) => {
  const queryClient = useQueryClient();
  const session = useTellescopeSession(providedSession);
  const mutationKey = useMemo(() => buildTellescopeQueryKey({ model, method }), [method, model]);
  const resolvedConfig = useTellescopeTanstackConfigContext({
    autoCacheUpdateStrategy,
    optimisticUpdates,
  });

  const mutation = useMutation<
    ReturnForMethod<Model, Method>,
    TError,
    ParamsForMethod<Model, Method>,
    UseTellescopeMutationContext<TOnMutateResult, Model>
  >({
    mutationKey,
    mutationFn: (request) => {
      if (session === null) {
        throw new Error(
          "No Tellescope session provided for query. Mutations should not be executed is session is `null`"
        );
      }
      return executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      });
    },
    onMutate: async (request, context) => {
      const externalContext = await onMutate?.(request, context);
      const defaultInternalContext: TellescopeMutationInternalContext<Model> = {
        originalQueries: [],
      };

      // Only apply optimistic updates if the filtered config isn't disabled
      if (resolvedConfig.optimisticUpdates) {
        const onMutateResult = executeTellescopeMutationSideEffect({
          queryClient,
          model,
          method,
          strategy: resolvedConfig.autoCacheUpdateStrategy ?? "invalidate",
          sideEffect: "onMutate",
          request,
          context,
          session: session ?? undefined,
        });

        return {
          externalContext,
          internalContext: onMutateResult ?? defaultInternalContext,
        };
      } else {
        return {
          externalContext,
          internalContext: defaultInternalContext,
        };
      }
    },
    onSuccess: (response, request, onMutateResult, context) => {
      onSuccess?.(response, request, onMutateResult?.externalContext, context);
      const internalContext = onMutateResult?.internalContext;
      executeTellescopeMutationSideEffect<Model, Method, TStrategy>({
        queryClient,
        model,
        method,
        strategy: (resolvedConfig.autoCacheUpdateStrategy ?? "invalidate") as TStrategy,
        sideEffect: "onSuccess",
        request,
        response,
        onMutateResult: internalContext,
        context,
        session: session ?? undefined,
      });
    },
    onError: (error, request, onMutateResult, context) => {
      onError?.(error, request, onMutateResult?.externalContext, context);
      if (onMutateResult?.internalContext.originalQueries.length) {
        rollbackCacheUpdates({
          queryClient,
          originalQueries: onMutateResult.internalContext.originalQueries,
        });
      }
    },
    onSettled: (response, error, request, onMutateResult, context) =>
      onSettled?.(response, error, request, onMutateResult?.externalContext, context),
    ...tanstackOptions,
  });

  return addKeyToMutationResult(mutation, mutationKey);
};
